import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import { useLanguage } from '../LanguageContext';
import { buildMessageLink } from './messagesModel';
import './Marketplace.css';

const removeFavorite = (targetType, targetId) => api.delete(`/favorites/${targetType}/${targetId}`);

export default function ServiceDetail() {
  const { id } = useParams();
  const { language, copy, localizeCategory, localizeMethod, translatePhrase } = useLanguage();
  const [liveService, setLiveService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [notice, setNotice] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [favoriteSubmitting, setFavoriteSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [relatedTasks, setRelatedTasks] = useState([]);
  const texts = copy.services;
  const navigate = useNavigate();
  const [selectedPackageIndex, setSelectedPackageIndex] = useState(0);
  const [preferredTime, setPreferredTime] = useState('');
  const [bookingNote, setBookingNote] = useState('');
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const isLiveListing = id.startsWith('listing-');
  const detailText = language === 'zh' ? {
    loading: '加载中...',
    tabsLabel: '服务详情分区',
    descriptionTab: '服务说明',
    packagesTab: '服务套餐',
    reviewsTab: count => `评价（${count}）`,
    packagesTitle: '服务套餐',
    reviewSummary: count => `★ ${count} 条评价`,
    noReviews: '暂无评价，完成订单后会显示在这里。',
    noWrittenComment: '用户未留下文字评价。',
    unverified: '暂未完成平台认证',
    preferredTime: '期望服务时间',
    note: '补充说明',
    submitting: '提交中...',
    saved: '已收藏',
    report: '举报',
    reporting: '提交中...',
    browseCategory: '浏览该分类服务',
  } : {
    loading: 'Loading...',
    tabsLabel: 'Service detail sections',
    descriptionTab: 'Description',
    packagesTab: 'Packages',
    reviewsTab: count => `Reviews (${count})`,
    packagesTitle: 'Service packages',
    reviewSummary: count => `★ ${count} reviews`,
    noReviews: 'No reviews yet. Completed orders will appear here.',
    noWrittenComment: 'No written comment.',
    unverified: 'Not platform verified yet',
    preferredTime: 'Preferred time',
    note: 'Note',
    submitting: 'Submitting...',
    saved: 'Saved',
    report: 'Report',
    reporting: 'Submitting...',
    browseCategory: 'Browse this category',
  };

  useEffect(() => {
    setLiveService(null);
    if (!isLiveListing) {
      setMissing(true);
      setLoading(false);
      return;
    }
    const liveId = id.replace('listing-', '');
    setLoading(true);
    setMissing(false);
    api.get(`/marketplace/services/${liveId}`)
      .then(res => {
        setSelectedPackageIndex(0);
        setLiveService(res.data);
      })
      .catch(() => setMissing(true))
      .finally(() => setLoading(false));
  }, [id, isLiveListing]);

  useEffect(() => {
    if (!isLiveListing) {
      setSaved(false);
      return;
    }
    const targetId = Number(id.replace('listing-', ''));
    api.get('/favorites', { params: { targetType: 'service' } })
      .then(res => {
        const favorites = res.data.favorites || [];
        setSaved(favorites.some(item => Number(item.target_id) === targetId));
      })
      .catch(() => setSaved(false));
  }, [id, isLiveListing]);

  useEffect(() => {
    if (!isLiveListing) {
      setRelatedTasks([]);
      return;
    }
    const liveId = id.replace('listing-', '');
    let cancelled = false;
    api.get(`/marketplace/services/${liveId}/recommendations/tasks`)
      .then(res => {
        if (!cancelled) setRelatedTasks(res.data.tasks || []);
      })
      .catch(() => {
        if (!cancelled) setRelatedTasks([]);
      });
    return () => { cancelled = true; };
  }, [id, isLiveListing]);

  if (loading) return <div className="loading">{detailText.loading}</div>;
  if (missing || !liveService) return <Navigate to="/services" replace />;
  const service = liveService;
  const verificationBadges = Array.isArray(service.verified) ? service.verified : [];
  const isLiveUnverified = isLiveListing && service.providerVerificationStatus !== 'verified';
  const messageLink = buildMessageLink({
    peerId: liveService?.providerId,
    peerName: service.provider,
    peerSeed: service.provider || service.id,
  });
  const servicePackages = Array.isArray(service.packages) && service.packages.length
    ? service.packages
    : [{
        name: service.title,
        price: service.price,
        duration: service.availability,
        description: service.description,
      }];
  const serviceCases = Array.isArray(service.cases) ? service.cases : [];
  const serviceReviews = Array.isArray(service.reviews) ? service.reviews : [];
  const ratingDistribution = service.ratingDistribution || {};
  const reviewCount = Number(service.reviewCount ?? serviceReviews.length);
  const coverImage = service.coverImage || 'https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1600&q=82';
  const formatReviewDate = value => {
    if (!value) return '';
    const date = new Date(String(value).replace(' ', 'T'));
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US');
  };

  const showNotice = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 2600);
  };

  const submitReport = async () => {
    setReportSubmitting(true);
    try {
      const res = await api.post('/reports', {
        targetType: 'service',
        targetId: liveService.id,
        reason: '服务内容疑似违规',
      });
      showNotice(res.data.message || '举报已提交，平台会尽快处理');
    } catch (err) {
      showNotice(err.response?.data?.error || '举报提交失败，请稍后重试');
    } finally {
      setReportSubmitting(false);
    }
  };

  const toggleSaved = async () => {
    const targetType = 'service';
    const targetId = liveService.id;
    setFavoriteSubmitting(true);
    try {
      if (saved) {
        await removeFavorite(targetType, targetId);
        setSaved(false);
        showNotice('已取消收藏');
      } else {
        const res = await api.post('/favorites', { targetType, targetId });
        setSaved(true);
        showNotice(res.data.message || '已加入收藏');
      }
    } catch (err) {
      showNotice(err.response?.data?.error || '收藏操作失败，请稍后重试');
    } finally {
      setFavoriteSubmitting(false);
    }
  };

  const submitBooking = async e => {
    e.preventDefault();
    if (!isLiveListing || !liveService?.id) return;
    setBookingSubmitting(true);
    try {
      const res = await api.post(`/marketplace/services/${liveService.id}/bookings`, {
        packageIndex: selectedPackageIndex,
        preferredTime,
        note: bookingNote,
      });
      const checkout = res.data.checkout;
      if (checkout.requestId) {
        navigate(`/checkout?requestId=${encodeURIComponent(checkout.requestId)}`);
      } else {
        const params = new URLSearchParams({
          title: checkout.title || '',
          amount: String(checkout.amount || ''),
          description: checkout.description || '',
        });
        navigate(`/checkout?${params.toString()}`);
      }
    } catch (err) {
      showNotice(err.response?.data?.error || (language === 'zh' ? '预约提交失败，请稍后再试' : 'Booking request failed. Please try again later.'));
    } finally {
      setBookingSubmitting(false);
    }
  };

  return (
    <div className="market-page">
      <div className="container detail-layout">
        <main className={`detail-card service-detail-tab-${activeTab}`}>
          <div className="service-cover" style={{ backgroundImage: `url(${coverImage})` }} aria-label={service.title}>
            <div className="service-cover-overlay">
              <span>{localizeCategory(service.category)}</span>
              <strong>{service.provider}</strong>
            </div>
          </div>
          <div className="card-meta">
            <span className="pill">{localizeCategory(service.category)}</span>
            <span className="pill">{localizeMethod(service.method)}</span>
            <span className="pill">{translatePhrase(service.priceMode)}</span>
            <span className="pill">{service.area} · {service.distance}</span>
          </div>
          <h1 className="detail-title">{service.title}</h1>
          <p className="card-muted detail-lead-summary">{service.summary}</p>
          <div className="price-line" style={{ marginTop: 16 }}>
            <span className="price">{service.price}</span>
            <span className="card-muted">★ {service.rating} · {detailText.reviewSummary(reviewCount)} · {service.response}</span>
          </div>

          <div className="service-tabs" role="tablist" aria-label={detailText.tabsLabel}>
            <button type="button" role="tab" aria-selected={activeTab === 'description'} className={activeTab === 'description' ? 'active' : ''} onClick={() => setActiveTab('description')}>{detailText.descriptionTab}</button>
            <button type="button" role="tab" aria-selected={activeTab === 'packages'} className={activeTab === 'packages' ? 'active' : ''} onClick={() => setActiveTab('packages')}>{detailText.packagesTab}</button>
            <button type="button" role="tab" aria-selected={activeTab === 'reviews'} className={activeTab === 'reviews' ? 'active' : ''} onClick={() => setActiveTab('reviews')}>{detailText.reviewsTab(reviewCount)}</button>
          </div>

          {activeTab === 'description' && <section className="detail-section">
            <h2>{texts.serviceDescription}</h2>
            <p className="card-muted">{service.description}</p>
            <div className="service-availability-row"><strong>{texts.availability}</strong><span className="card-muted">{service.availability}</span></div>
          </section>}

          {activeTab === 'packages' && <section className="detail-section">
            <h2>{detailText.packagesTitle}</h2>
            <div className="service-package-list">
              {servicePackages.map((item, index) => (
                <label className={`service-package-option ${selectedPackageIndex === index ? 'selected' : ''}`} key={`${item.name}-${index}`}>
                  {isLiveListing && liveService?.id && (
                    <input
                      type="radio"
                      name="service-package"
                      checked={selectedPackageIndex === index}
                      onChange={() => setSelectedPackageIndex(index)}
                    />
                  )}
                  <span>
                    <strong>{item.name}</strong>
                    <small>{item.duration}</small>
                  </span>
                  <em>{item.price}</em>
                  <p>{item.description}</p>
                </label>
              ))}
            </div>
          </section>}

          {activeTab === 'reviews' && <section className="detail-section service-reviews-section">
            <div className="review-summary">
              <div className="review-score"><strong>{Number(service.rating || 0).toFixed(1)}</strong><span>{detailText.reviewSummary(reviewCount)}</span></div>
              <div className="review-distribution">
                {[5, 4, 3, 2, 1].map(star => {
                  const count = Number(ratingDistribution[star] || 0);
                  const width = reviewCount ? `${Math.round((count / reviewCount) * 100)}%` : '0%';
                  return <div className="review-distribution-row" key={star}><span>{star} ★</span><span className="review-bar"><i style={{ width }} /></span><em>{count}</em></div>;
                })}
              </div>
            </div>
            {serviceReviews.length > 0 ? <div className="service-review-list">
              {serviceReviews.map(review => <article className="service-review" key={review.id}>
                <div className="service-review-head"><strong>{review.reviewer}</strong><span className="service-review-stars">{'★'.repeat(Math.max(0, Math.min(5, Number(review.rating || 0))))}</span><time>{formatReviewDate(review.date)}</time></div>
                {review.task && <small>{review.task}</small>}
                <p>{review.comment || detailText.noWrittenComment}</p>
              </article>)}
            </div> : <div className="service-review-empty">{detailText.noReviews}</div>}
          </section>}

          {serviceCases.length > 0 && (
            <section className="detail-section">
              <h2>{texts.caseStudies}</h2>
              <div className="case-list">
                {serviceCases.map(item => <div className="case-tile" key={item}>{item}</div>)}
              </div>
            </section>
          )}

          <section className="detail-section">
            <h2>{texts.providerTrust}</h2>
            <div className="trust-row">
              {verificationBadges.map(v => <span key={v} className="trust-badge">{v}</span>)}
              {isLiveUnverified && <span className="trust-badge muted">{detailText.unverified}</span>}
            </div>
            <p className="card-muted" style={{ marginTop: 10 }}>{service.provider} {texts.providerSummary} {service.orders} {texts.serviceCount}, {texts.scoreLabel} {service.rating}.</p>
          </section>

          {isLiveListing && relatedTasks.length > 0 && (
            <section className="detail-section">
              <h2>{language === 'zh' ? '相关需求' : 'Related requirements'}</h2>
              <div className="related-task-list">
                {relatedTasks.map(task => (
                  <Link key={task.id} className="related-task-card" to={`/tasks/${task.id}`}>
                    <strong>{task.title}</strong>
                    <p>{task.description}</p>
                    <div className="related-task-meta">
                      <span>{localizeCategory(task.category)}</span>
                      <span>¥ {Number(task.budget_max || 0).toLocaleString()}</span>
                      <span>{Number(task.submit_count || 0)} {language === 'zh' ? '应征' : 'submissions'}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>

        <aside className="detail-card">
          <h2>{service.provider}</h2>
          <p className="card-muted" style={{ margin: '8px 0 16px' }}>{texts.serviceRange}: {service.area}, {localizeMethod(service.method)} {texts.delivery}</p>
          {notice && <div className="detail-notice">{notice}</div>}
          <div className="side-actions">
            {isLiveListing ? (
              <>
                <Link className="btn btn-primary" to={messageLink}>{texts.contactProvider}</Link>
                <form className="booking-form" onSubmit={submitBooking}>
                  <label>
                    {detailText.preferredTime}
                    <input required value={preferredTime} onChange={e => setPreferredTime(e.target.value)} />
                  </label>
                  <label>
                    {detailText.note}
                    <textarea value={bookingNote} onChange={e => setBookingNote(e.target.value)} />
                  </label>
                  <button className="btn btn-outline" type="submit" disabled={bookingSubmitting}>
                    {bookingSubmitting ? detailText.submitting : texts.bookService}
                  </button>
                </form>
                <button className="btn btn-ghost" type="button" onClick={toggleSaved} disabled={favoriteSubmitting}>{saved ? detailText.saved : texts.saveService}</button>
                <button className="btn btn-outline" type="button" onClick={submitReport} disabled={reportSubmitting}>
                  {reportSubmitting ? detailText.reporting : detailText.report}
                </button>
              </>
            ) : (
              <Link className="btn btn-primary" to={`/services?categories=${encodeURIComponent(service.category || '')}`}>
                {detailText.browseCategory}
              </Link>
            )}
          </div>
          <div className="detail-section">
            <h2>{texts.beforeOrder}</h2>
            <p className="card-muted">{texts.beforeOrderText}</p>
          </div>
          <div className="safety-box">{service.safety}</div>
        </aside>
      </div>
    </div>
  );
}
